import { useMemo, useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { DesignContext } from 'contexts/DesignContext'

import objectPath from 'object-path'
import { Form, Col, Row } from 'react-bootstrap'
import Label from './Label'
import { createUischemaStyle } from './Style'
import httpClient from 'utility/httpClient.util'
import {
  cancelDebounceCallback,
  debounceCallback,
} from 'utility/debounceCallback.util'
import {
  FAVORITE_SUGGESTION_SCOPES,
  filterFavoriteTemplatesByKeyword,
  getFavoriteTemplateSuggestionValue,
  insertSuggestionValue,
  mergeFavoriteTemplateRows,
  normalizeFavoriteTemplateRows,
} from './favorite/suggestionFormula'

let textareaDebounceId = 0

const useTextareaAutoHeight = (ref, rows) => {
  useEffect(() => {
    const listener = () => {
      if (ref.current) {
        ref.current.style.minHeight = rows * 26 + 'px'
        ref.current.style.height = 'auto'
        ref.current.style.height = ref.current.scrollHeight + 'px'
      }
    }
    if (ref.current) {
      ref.current.addEventListener('input', listener)
      listener()
    }
    return () => {
      if (ref.current) {
        ref.current.removeEventListener('input', listener)
      }
    }
  }, [ref?.current])
}

const CustomTextarea = (props) => {
  let {
    value,
    uischema,
    updateData,
    errors = {},
    uiKey,
    label,
    setIsOnChange,
    fieldName,
    fieldKey,
  } = props
  let errorArray = objectPath.get(errors, uiKey) || []
  let {
    format,
    name,
    key,
    elementWidth,
    placeholder,
    minLength,
    maxLength,
    readonly,
    controlId,
    suffixText,
    require,
    rows = 3,
  } = uischema

  const designContext = useContext(DesignContext)
  const { elementAction, jsonForm, openModal } = designContext
  const isLocked = objectPath.get(elementAction, ['isLockedEdit'])
  let hasEnable = objectPath.has(elementAction, ['isEnable', key])
  let isEnable = objectPath.get(elementAction, ['isEnable', key])
  let isReadOnly = useMemo(() => {
    if (isLocked) return isLocked
    if (hasEnable) {
      return isEnable === false ? true : false
    }
    return uischema.readonly
  }, [isEnable, uischema.readonly])

  let hasRequire = objectPath.has(elementAction, ['isRequire', key])
  let requireMessage = objectPath.get(elementAction, ['isRequire', key])
  let showRequireMessage = hasRequire
    ? typeof requireMessage !== 'boolean'
    : require
  let isRequire = hasRequire || require
  let showRequire = value ? false : isRequire ? true : false

  let hasWarning = objectPath.has(elementAction, ['isWarning', key])
  let warningMessage = objectPath.get(elementAction, ['isWarning', key])
  let showWarningMessage = hasWarning
    ? typeof warningMessage !== 'boolean'
    : warningMessage

  const textareaRef = useRef(null)
  const suggestionInputRef = useRef(null)
  useTextareaAutoHeight(textareaRef, rows)
  const favoriteTemplateTypeCode = objectPath.get(
    uischema,
    'favorite.favoriteTemplateTypeCode',
  )
  const isSuggestFormula =
    !!objectPath.get(uischema, 'favorite.suggestFormula') &&
    !!favoriteTemplateTypeCode &&
    !isReadOnly
  const favoriteKey = objectPath.get(uischema, 'favorite.key')
  const favoriteSetting = useMemo(() => {
    const favoriteList = objectPath.get(jsonForm, 'favorites') || []
    return favoriteList.find((item) => item.key === favoriteKey) || {}
  }, [favoriteKey, jsonForm])
  const [favoriteTemplates, setFavoriteTemplates] = useState([])
  const [suggestionList, setSuggestionList] = useState([])
  const [suggestionKeyword, setSuggestionKeyword] = useState('')
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0)
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false)
  const [suggestionPosition, setSuggestionPosition] = useState(null)
  const isFavoriteTemplatesLoadingRef = useRef(false)
  const textareaSelectionRef = useRef(null)

  const uiStyle = useMemo(
    () => createUischemaStyle({ uischema, showRequire }),
    [uischema, showRequire]
  )

  let dataValue = useMemo(() => {
    if (value && typeof value === 'object' && Object.values(value).length > 0) {
      return Object.values(value).join(', ')
    }

    return value
  }, [value])
  const [localValue, setLocalValue] = useState(dataValue || '')
  const [debounceInputName] = useState(() => {
    textareaDebounceId += 1
    return `textarea-update-${fieldKey}-${textareaDebounceId}`
  })
  const updateTextareaData = (value) => {
    updateData(value, true)
  }

  useEffect(() => {
    return () => cancelDebounceCallback(debounceInputName)
  }, [debounceInputName])

  useEffect(() => {
    setLocalValue(dataValue || '')
  }, [dataValue])

  const clearFormulaSuggestion = () => {
    setSuggestionList([])
    setActiveSuggestionIndex(0)
    setSuggestionPosition(null)
  }

  const updateSuggestionPosition = () => {
    if (!suggestionInputRef.current) return
    const rect = suggestionInputRef.current.getBoundingClientRect()
    setSuggestionPosition({
      left: rect.left,
      top: rect.bottom,
      width: rect.width,
    })
  }

  const updateFormulaSuggestion = (
    nextKeyword,
    templateList = favoriteTemplates,
  ) => {
    if (!isSuggestFormula) {
      clearFormulaSuggestion()
      return
    }

    const nextSuggestionList = filterFavoriteTemplatesByKeyword(
      templateList,
      nextKeyword,
    ).slice(0, 8)

    setSuggestionList(nextSuggestionList)
    setActiveSuggestionIndex(0)
    updateSuggestionPosition()
  }

  const isSuggestionOpen =
    suggestionList.length > 0 ||
    (isSuggestionLoading && !!`${suggestionKeyword || ''}`.trim())

  const loadFavoriteTemplates = async ({
    force = false,
    keyword = suggestionKeyword,
  } = {}) => {
    if (!isSuggestFormula || isFavoriteTemplatesLoadingRef.current) {
      return
    }
    if (!force && favoriteTemplates.length > 0) return

    isFavoriteTemplatesLoadingRef.current = true
    setIsSuggestionLoading(true)

    try {
      const templateLists = await Promise.all(
        FAVORITE_SUGGESTION_SCOPES.map(async (scope) => {
          const response = await httpClient
            .get(`/core/favorite-template/${favoriteTemplateTypeCode}/${scope}`)
            .catch(() => null)
          return normalizeFavoriteTemplateRows(response?.data)
        }),
      )
      const nextFavoriteTemplates = mergeFavoriteTemplateRows(templateLists)
      setFavoriteTemplates(nextFavoriteTemplates)

      if (suggestionInputRef.current && isSuggestFormula) {
        const currentKeyword = suggestionInputRef.current.value || keyword
        updateFormulaSuggestion(currentKeyword, nextFavoriteTemplates)
      }
    } finally {
      isFavoriteTemplatesLoadingRef.current = false
      setIsSuggestionLoading(false)
    }
  }

  const updateTextareaSelection = () => {
    if (!textareaRef.current) return

    textareaSelectionRef.current = {
      start: textareaRef.current.selectionStart,
      end: textareaRef.current.selectionEnd,
    }
  }

  const selectFormulaSuggestion = (template) => {
    const textarea = textareaRef.current
    const currentValue = textarea?.value || dataValue || ''
    const selection = textareaSelectionRef.current
    const replacement = getFavoriteTemplateSuggestionValue(
      template,
      uischema,
      favoriteSetting,
    )

    if (!replacement) {
      clearFormulaSuggestion()
      return
    }

    const nextData = insertSuggestionValue(currentValue, selection, replacement)
    setLocalValue(nextData.value)
    cancelDebounceCallback(debounceInputName)
    updateTextareaData(nextData.value)
    if (setIsOnChange) {
      setIsOnChange(true)
    }
    setSuggestionKeyword('')
    clearFormulaSuggestion()

    const focusTextarea = () => {
      if (!textareaRef.current) return
      textareaRef.current.focus()
      textareaRef.current.setSelectionRange(
        nextData.selectionStart,
        nextData.selectionStart,
      )
      updateTextareaSelection()
    }

    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(focusTextarea)
    } else {
      setTimeout(focusTextarea, 0)
    }
  }

  const onFormulaKeyDown = (event) => {
    if (!isSuggestFormula || suggestionList.length === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveSuggestionIndex((activeIndex) =>
        activeIndex >= suggestionList.length - 1 ? 0 : activeIndex + 1,
      )
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveSuggestionIndex((activeIndex) =>
        activeIndex <= 0 ? suggestionList.length - 1 : activeIndex - 1,
      )
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault()
      selectFormulaSuggestion(suggestionList[activeSuggestionIndex])
    } else if (event.key === 'Escape') {
      clearFormulaSuggestion()
    }
  }

  const onSuggestionSearchBlur = () => {
    setTimeout(clearFormulaSuggestion, 150)
  }

  const onSuggestionSearchChange = (event) => {
    const nextKeyword = event.target.value
    setSuggestionKeyword(nextKeyword)
    loadFavoriteTemplates({ keyword: nextKeyword })
    updateFormulaSuggestion(nextKeyword)
  }

  const clearSuggestionKeyword = () => {
    setSuggestionKeyword('')
    clearFormulaSuggestion()
    if (suggestionInputRef.current) {
      suggestionInputRef.current.focus()
    }
  }

  useEffect(() => {
    setFavoriteTemplates([])
    setSuggestionKeyword('')
    clearFormulaSuggestion()
  }, [favoriteTemplateTypeCode, isSuggestFormula])

  useEffect(() => {
    if (!isSuggestionOpen) return

    updateSuggestionPosition()
    window.addEventListener('resize', updateSuggestionPosition)
    window.addEventListener('scroll', updateSuggestionPosition, true)

    return () => {
      window.removeEventListener('resize', updateSuggestionPosition)
      window.removeEventListener('scroll', updateSuggestionPosition, true)
    }
  }, [isSuggestionOpen])

  const renderSuggestionList = () => {
    if (
      !isSuggestFormula ||
      !isSuggestionOpen ||
      !suggestionPosition ||
      typeof document === 'undefined'
    ) {
      return null
    }

    return createPortal(
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #dfe7ef',
          borderRadius: 4,
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.12)',
          left: suggestionPosition.left,
          maxHeight: 220,
          overflowY: 'auto',
          position: 'fixed',
          top: suggestionPosition.top,
          width: suggestionPosition.width,
          zIndex: 2050,
        }}
      >
        {isSuggestionLoading && suggestionList.length === 0 ? (
          <div className="text-muted px-2 py-2">กำลังโหลดสูตร...</div>
        ) : (
          suggestionList.map((template, index) => {
            const isActive = activeSuggestionIndex === index
            return (
              <div
                key={template.favoriteTemplateId || `${template.name}-${index}`}
                className="px-2 py-2"
                style={{
                  backgroundColor: isActive ? '#f1f6ff' : '#fff',
                  cursor: 'pointer',
                  overflowWrap: 'anywhere',
                }}
                onMouseEnter={() => setActiveSuggestionIndex(index)}
                onMouseDown={(event) => {
                  event.preventDefault()
                  selectFormulaSuggestion(template)
                }}
              >
                <div className="font-weight-bold">{template.name || '-'}</div>
                <div className="text-muted small">
                  {getFavoriteTemplateSuggestionValue(
                    template,
                    uischema,
                    favoriteSetting,
                  ) || '-'}
                </div>
              </div>
            )
          })
        )}
      </div>,
      document.body,
    )
  }

  return (
    <Form.Group className="mb-0" controlId={controlId}>
      <Row className="w-100 mx-0">
        <Label
          {...props}
          className="d-flex mt-2"
          uischema={uischema}
          label={label}
          isRequire={isRequire}
          isEnable={!isReadOnly}
        />
        <Col
          sm={elementWidth}
          className="mt-1 p-0"
          style={{
            position: isSuggestionOpen ? 'relative' : 'static',
            zIndex: isSuggestionOpen ? 1050 : 'auto',
          }}
        >
          <div
            style={{
              position: 'relative',
              zIndex: isSuggestionOpen ? 1050 : 'auto',
            }}
          >
            {isSuggestFormula && (
              <div className="input-group mb-1">
                <div className="input-group-prepend">
                  <span
                    className="input-group-text bg-white"
                    style={{
                      borderBottomLeftRadius: '0.25rem',
                      borderTopLeftRadius: '0.25rem',
                    }}
                  >
                    <i className="feather icon-search" />
                  </span>
                </div>
                <Form.Control
                  ref={suggestionInputRef}
                  autoComplete="off"
                  placeholder="ค้นหาสูตร"
                  style={{
                    borderBottomRightRadius: '0.25rem',
                    borderTopRightRadius: '0.25rem',
                    paddingRight: suggestionKeyword ? 32 : undefined,
                  }}
                  value={suggestionKeyword}
                  onFocus={() => {
                    loadFavoriteTemplates({ force: true })
                    updateFormulaSuggestion(suggestionKeyword)
                  }}
                  onClick={() => {
                    updateFormulaSuggestion(suggestionKeyword)
                  }}
                  onKeyDown={onFormulaKeyDown}
                  onBlur={onSuggestionSearchBlur}
                  onChange={onSuggestionSearchChange}
                />
                {suggestionKeyword && (
                  <button
                    type="button"
                    className="btn btn-link p-0 text-muted"
                    style={{
                      height: '100%',
                      position: 'absolute',
                      right: 8,
                      top: 0,
                      zIndex: 4,
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault()
                      clearSuggestionKeyword()
                    }}
                  >
                    <i className="feather icon-x" />
                  </button>
                )}
              </div>
            )}
            <Form.Control
              id={fieldKey}
              name={fieldName}
              style={uiStyle}
              as="textarea"
              ref={textareaRef}
              autoComplete="off"
              rows={rows}
              placeholder={placeholder}
              minLength={minLength}
              maxLength={maxLength}
              value={localValue}
              onFocus={updateTextareaSelection}
              onClick={updateTextareaSelection}
              onKeyUp={updateTextareaSelection}
              onSelect={updateTextareaSelection}
              onChange={(e) => {
                const nextValue = e.target.value
                setLocalValue(nextValue)
                if (openModal) {
                  cancelDebounceCallback(debounceInputName)
                  updateTextareaData(nextValue)
                } else {
                  debounceCallback(
                    updateTextareaData,
                    [nextValue],
                    debounceInputName,
                    250,
                  )
                }
                if (setIsOnChange) {
                  setIsOnChange(true)
                }
                updateTextareaSelection()
              }}
              onBlur={(e) => {
                cancelDebounceCallback(debounceInputName)
                updateTextareaData(e.target.value)
                updateTextareaSelection()
              }}
              readOnly={isReadOnly}
            />
            {renderSuggestionList()}
          </div>
        </Col>
        {suffixText && (
          <Col className="d-flex mt-2 pr-0" sm="auto">
            <Form.Label className={`m-0 w-100`}>{suffixText}</Form.Label>
          </Col>
        )}
      </Row>
      <ul className="m-0 mt-2">
        {showRequire && showRequireMessage && requireMessage && (
          <li className="text-danger">{requireMessage}</li>
        )}
        {showWarningMessage && warningMessage && (
          <li className="text-danger">{warningMessage}</li>
        )}
        {errorArray.map((error, index) => (
          <li key={`error-${uiKey}-${index + 1}`} className="text-danger">
            {error}
          </li>
        ))}
      </ul>
    </Form.Group>
  )
}

export default CustomTextarea
